/**
 * Summary overlay — Phase 3 overlay (DOM).
 * Shown when state.won !== null (battle end: victory or defeat).
 * Replaces drawOverlay() in canvas-render-adapter.
 */

import type { GameStateView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

export interface SummaryOverlayCallbacks {
  onReturnToMap?: () => void;
}

export interface SummaryOverlay {
  update(state: GameStateView): void;
  destroy(): void;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function createSummaryOverlay(
  container: HTMLElement,
  cbs: SummaryOverlayCallbacks
): SummaryOverlay {
  const el = document.createElement("div");
  el.className = "hud-summary";
  el.style.display = "none";
  // Click on backdrop → return to map
  el.addEventListener("click", (e) => {
    if (e.target === el) cbs.onReturnToMap?.();
  });
  container.appendChild(el);

  const panel = document.createElement("div");
  panel.className = "hud-summary__panel hud-panel";
  el.appendChild(panel);

  const titleEl = document.createElement("div");
  titleEl.className = "hud-summary__title";
  panel.appendChild(titleEl);

  // Stats table
  const statsEl = document.createElement("div");
  statsEl.className = "hud-summary__stats";
  panel.appendChild(statsEl);

  // XP row (win only)
  const xpRowEl = document.createElement("div");
  xpRowEl.className = "hud-summary__xp-row";
  panel.appendChild(xpRowEl);

  // Return button
  const returnBtn = document.createElement("button");
  returnBtn.className = "hud-summary__return-btn";
  returnBtn.addEventListener("click", () => cbs.onReturnToMap?.());
  panel.appendChild(returnBtn);

  function update(state: GameStateView): void {
    const visible = state.scene === "game" && state.won !== null && state.won !== undefined;
    el.style.display = visible ? "" : "none";
    if (!visible) return;

    const won = state.won as boolean;
    const summary = state.battleSummary;

    // Panel title color class
    panel.classList.toggle("hud-summary__panel--win", won);
    panel.classList.toggle("hud-summary__panel--lose", !won);

    titleEl.textContent = t(won ? "summary.title.win" : "summary.title.lose");
    titleEl.classList.toggle("hud-summary__title--win", won);
    titleEl.classList.toggle("hud-summary__title--lose", !won);

    // Stats rows
    statsEl.innerHTML = "";
    if (summary) {
      const rows: [string, string][] = [
        [t("summary.kills"),    String(summary.killsThisBattle)],
        [t("summary.banishes"), String(summary.banishesThisBattle)],
        [t("summary.bombs"),    String(summary.bombsCastThisBattle)],
        [t("summary.earlyCalls"), String(summary.earlyHurryCount)],
        [t("summary.earlyLight"), String(summary.manaFromEarlyHurry)],
        [t("summary.duration"), formatDuration(summary.durationMs)],
      ];
      for (const [label, value] of rows) {
        const row = document.createElement("div");
        row.className = "hud-summary__stat-row";
        const lbl = document.createElement("span");
        lbl.className = "hud-summary__stat-label";
        lbl.textContent = label;
        const val = document.createElement("span");
        val.className = "hud-summary__stat-value";
        val.textContent = value;
        row.appendChild(lbl);
        row.appendChild(val);
        statsEl.appendChild(row);
      }
    }

    // XP row
    if (won && summary?.earnedXp !== null && summary?.earnedXp !== undefined) {
      xpRowEl.style.display = "";
      xpRowEl.innerHTML = "";
      const xpLbl = document.createElement("span");
      xpLbl.className = "hud-summary__xp-label";
      xpLbl.textContent = `${t("summary.xpEarned")}:`;
      const xpVal = document.createElement("span");
      xpVal.className = "hud-summary__xp-value";
      xpVal.textContent = `+${summary!.earnedXp} XP`;
      xpRowEl.appendChild(xpLbl);
      xpRowEl.appendChild(xpVal);
    } else {
      xpRowEl.style.display = "none";
    }

    returnBtn.textContent = t("summary.retreat");
  }

  function destroy(): void {
    el.remove();
  }

  return { update, destroy };
}
